// Thin AI client. In production it talks to our own /api/chat proxy, never to
// Anthropic directly. The proxy is responsible for holding the API key,
// rate-limiting, applying prompt caching, and shaping the response.
//
// In dev or offline modes we fall back to the mock client.

import type { ChatMessage, HskLevel, LibraryWord, Persona, Story, Suggestion, Word } from '../types';
import { getAuth } from '../auth/auth';
import { buildSystemPrompt, GREETING_USER_PROMPT, SUGGESTION_SYSTEM_PROMPT } from './prompts';
import { parseAIArray, parseAIObject } from './parse';

/** Standard headers + bearer token when the user is signed in. */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const jwt = getAuth().current()?.jwt;
  if (jwt) headers.authorization = `Bearer ${jwt}`;
  return headers;
}

export interface AIReply {
  hz: string;
  py: string;
  en: string;
  words: Word[];
}

// Belt-and-suspenders: even with the prompt rule against emoji, some models
// occasionally slip a pictograph into hz. The Hanzi serif font has no glyph
// for them, so they render as tofu boxes in chat. Strip on parse.
const EMOJI_RE = /[\p{Extended_Pictographic}\u{FE0F}\u{200D}]/gu;
const stripEmoji = (s: string): string => s.replace(EMOJI_RE, '').replace(/\s{2,}/g, ' ').trim();
function sanitizeReply(r: AIReply): AIReply {
  return {
    hz: stripEmoji(r.hz),
    py: stripEmoji(r.py),
    en: stripEmoji(r.en),
    words: r.words.map((w) => ({ hz: stripEmoji(w.hz), py: stripEmoji(w.py), en: stripEmoji(w.en) })),
  };
}

export interface ChatRequest {
  persona: Persona;
  history: ChatMessage[];
  library: LibraryWord[];
  lastStory?: Story | null;
  /** Durable user facts surfaced in the system prompt; see PersonaMemory. */
  memoryFacts?: string[];
  /**
   * Hard HSK cap for the AI reply. Server validates char-by-char and retries
   * once if violated. Null/undefined means no cap (today's behavior).
   */
  hskCap?: HskLevel | null;
}

export interface SuggestRequest {
  persona: Persona;
  history: ChatMessage[];
}

export interface GreetRequest {
  persona: Persona;
  /** Pass-through to the chat endpoint so library chars are exempt from HSK
   *  validation, same as normal chat turns. */
  library?: LibraryWord[];
  /** Hard HSK cap, same semantics as chat(). */
  hskCap?: HskLevel | null;
}

export interface ExtractMemoryRequest {
  persona: Persona;
  /** Existing facts so the model can update rather than overwrite. */
  currentFacts: string[];
  history: ChatMessage[];
}

export interface LookupRequest {
  /** Tap-mode: full sentence context. */
  context?: string;
  /** Tap-mode: substring the user tapped. */
  selection?: string;
  /** Free-input mode: raw user text from the library + dialog. */
  raw?: string;
}

export interface AIClient {
  chat(req: ChatRequest, signal?: AbortSignal): Promise<AIReply>;
  suggest(req: SuggestRequest, signal?: AbortSignal): Promise<Suggestion[]>;
  extractMemory(req: ExtractMemoryRequest, signal?: AbortSignal): Promise<string[]>;
  lookup(req: LookupRequest, signal?: AbortSignal): Promise<Word>;
  /** One-shot in-character opener for a freshly created custom persona. */
  greet(req: GreetRequest, signal?: AbortSignal): Promise<AIReply>;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

class HttpAIClient implements AIClient {
  async chat(req: ChatRequest, signal?: AbortSignal): Promise<AIReply> {
    const { cached, dynamic } = buildSystemPrompt({
      persona: req.persona,
      library: req.library,
      lastStory: req.lastStory ?? null,
      memoryFacts: req.memoryFacts,
    });

    // Sliding window: only the last 20 messages travel to the model. Past that
    // input tokens grow linearly with conversation length, and the persona
    // doesn't gain much by re-reading turn 47 from yesterday. Durable user
    // facts will live in a separate memory layer (see TODO) so the persona
    // still "remembers" who you are even after old turns scroll out.
    const HISTORY_WINDOW = 20;
    const windowed = req.history.slice(-HISTORY_WINDOW);

    const body = {
      systemCached: cached,
      systemDynamic: dynamic,
      messages: windowed.map((m) => ({
        role: m.from === 'me' ? 'user' : 'assistant',
        content:
          m.from === 'me'
            ? m.hz
            : JSON.stringify({ hz: m.hz, py: m.py, en: m.en, words: m.words }),
      })),
      // Library is needed server-side too so chars from saved words don't
      // count as HSK violations — the learner has already met them.
      libraryHz: req.library.map((w) => w.hz),
      ...(req.hskCap ? { hskCap: req.hskCap } : {}),
    };

    const res = await fetch(`${API_BASE}/chat`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Chat request failed (${res.status})`);
    }

    const { text } = (await res.json()) as { text: string };
    const parsed = parseAIObject<AIReply>(text);
    if (!parsed.hz) throw new Error('Reply missing hz field');
    return sanitizeReply({
      hz: parsed.hz,
      py: parsed.py ?? '',
      en: parsed.en ?? '',
      words: Array.isArray(parsed.words) ? parsed.words : [],
    });
  }

  async greet(req: GreetRequest, signal?: AbortSignal): Promise<AIReply> {
    // Reuses /api/chat with a one-message history: a synthetic user prompt
    // asking for the opener. The assistant's reply IS the persona's first
    // line. The synthetic user turn is discarded by the caller — only the
    // assistant reply is persisted into chat state.
    const { cached, dynamic } = buildSystemPrompt({
      persona: req.persona,
      library: req.library ?? [],
    });

    const body = {
      systemCached: cached,
      systemDynamic: dynamic,
      messages: [{ role: 'user' as const, content: GREETING_USER_PROMPT }],
      libraryHz: (req.library ?? []).map((w) => w.hz),
      ...(req.hskCap ? { hskCap: req.hskCap } : {}),
    };

    const res = await fetch(`${API_BASE}/chat`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
      credentials: 'include',
    });

    if (!res.ok) throw new Error(`Greeting request failed (${res.status})`);

    const { text } = (await res.json()) as { text: string };
    const parsed = parseAIObject<AIReply>(text);
    if (!parsed.hz) throw new Error('Greeting missing hz field');
    return sanitizeReply({
      hz: parsed.hz,
      py: parsed.py ?? '',
      en: parsed.en ?? '',
      words: Array.isArray(parsed.words) ? parsed.words : [],
    });
  }

  async suggest(req: SuggestRequest, signal?: AbortSignal): Promise<Suggestion[]> {
    const body = {
      system: `${req.persona.systemPrompt}\n\n${SUGGESTION_SYSTEM_PROMPT}`,
      // Compact context: last 4 turns is plenty for suggestion quality.
      messages: req.history.slice(-4).map((m) => ({
        role: m.from === 'me' ? 'user' : 'assistant',
        content: `${m.hz} (${m.en})`,
      })),
    };

    const res = await fetch(`${API_BASE}/suggest`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Suggest request failed (${res.status})`);
    }

    const { text } = (await res.json()) as { text: string };
    return parseAIArray<Suggestion>(text)
      .slice(0, 3)
      .map((s) => ({ hz: stripEmoji(s.hz), py: stripEmoji(s.py), en: stripEmoji(s.en) }));
  }

  async extractMemory(req: ExtractMemoryRequest, signal?: AbortSignal): Promise<string[]> {
    // Compact transcript: last 30 messages, hz only for the user, hz+en for
    // the persona. Plenty for the model to spot durable facts; cheap on input.
    const history = req.history.slice(-30).map((m) => ({
      role: (m.from === 'me' ? 'user' : 'assistant') as 'user' | 'assistant',
      content: m.from === 'me' ? m.hz : `${m.hz} (${m.en})`,
    }));

    const body = {
      personaName: `${req.persona.name} (${req.persona.en})`,
      currentFacts: req.currentFacts,
      history,
    };

    const res = await fetch(`${API_BASE}/extract-memory`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
      credentials: 'include',
    });

    if (!res.ok) throw new Error(`Memory extract failed (${res.status})`);
    const { facts } = (await res.json()) as { facts: string[] };
    return Array.isArray(facts) ? facts.slice(0, 12) : [];
  }

  async lookup(req: LookupRequest, signal?: AbortSignal): Promise<Word> {
    const body: Record<string, unknown> = {};
    if (req.context !== undefined) body.context = req.context;
    if (req.selection !== undefined) body.selection = req.selection;
    if (req.raw !== undefined) body.raw = req.raw;

    const res = await fetch(`${API_BASE}/lookup`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal,
      credentials: 'include',
    });

    if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
    const data = (await res.json()) as Word;
    if (!data?.hz) throw new Error('Lookup returned no hz');
    return { hz: stripEmoji(data.hz), py: stripEmoji(data.py), en: stripEmoji(data.en) };
  }
}

let cached: AIClient | null = null;

/** Lazily resolve the live client; swappable in tests via setAIClient. */
export function getAIClient(): AIClient {
  if (!cached) cached = new HttpAIClient();
  return cached;
}

export function setAIClient(client: AIClient): void {
  cached = client;
}
